import { SearchPair, useRoles } from './useRoles';
import useOrganisationTabs from './useOrganisationTabs';
import isInCCLiframe from '../../../utils/isInCCLiframe';
import {
  Role,
  RoleSelectionViewModel,
} from '../../../apollo/queries/getAllRoles.queries';
import { RoleSelection } from '../roles.types';
import { Member } from '../../../apollo/queries';

interface Props {
  selectedRoles: RoleSelection[];
  isOrgMember: boolean;
  user: Member | null;

  setSelectedRoles(roles: any): void;

  setSelectedOrgs(orgs: string[]): void;
}

const useOrgRow = ({
  setSelectedRoles,
  isOrgMember,
  user,
  selectedRoles,
  setSelectedOrgs,
}: Props) => {
  const {
    onCategorySelect,
    selectedCategory,
    getRolesSelectionByOrg,
    fetchRolesSelection,
  } = useRoles();

  const {
    organisations,
    orgTabs,
    updateOrganisationForRow,
    addOrganisationRow,
    removeOrganisationRow,
  } = useOrganisationTabs({
    isOrgMember,
    user,
    fetchRolesSelection,
    setSelectedOrgs,
  });

  const ignoredRoleCategories = ['EMarking'];
  const ignoredRoles = isInCCLiframe
    ? ['CCL Super Admin', 'Developer', 'Authoring Admin']
    : [];

  const selectedRolesForSearch = (orgCode: string) =>
    selectedRoles
      .filter((role) => role.org === orgCode)
      .map((role) => ({
        key: role.id,
        value: role.name,
      }));

  const rolesForRoleSelection = (orgCode: string) =>
    selectedCategory.roles
      .filter((role: Role) => !ignoredRoles.includes(role.name))
      .map((role: Role) => ({
        id: role.id,
        title: role.name,
        checked: !!selectedRolesForSearch(orgCode).find(
          (r) => r.key === role.id,
        ),
        description: role.description,
        categoryName: selectedCategory.categoryName,
      }));

  const onSearch = (updatedRoles: SearchPair[], orgCode: string) => {
    const rolesExcludedOrg = selectedRoles.filter((r) => r.org !== orgCode);

    const newSelected = updatedRoles.map((role: SearchPair) => ({
      id: role.key,
      name: role.value,
      org: orgCode,
    }));

    setSelectedRoles([...rolesExcludedOrg, ...newSelected]);
  };

  const onToggleSelectedRole = (role: RoleSelection) => {
    setSelectedRoles(role);
  };

  const removeRolesForOrganisation = (orgCode: string) => {
    const roles = selectedRoles.filter((sr) => sr.org !== orgCode);
    setSelectedRoles(roles);
  };

  const onDeleteOrgRowClick = (orgCode: string, index: number) => {
    removeOrganisationRow(index);
    removeRolesForOrganisation(orgCode);
  };

  const getAllRoles = (
    roleSelectionViewModel: RoleSelectionViewModel[],
    searchText: string,
  ): Role[] => {
    const allCategoryRoles: Role[] = [];

    roleSelectionViewModel.forEach(({ categories }) =>
      categories
        .filter((rc) => !ignoredRoleCategories.includes(rc.categoryName))
        .forEach((category) => allCategoryRoles.push(...category.roles)),
    );

    return allCategoryRoles.filter(
      ({ name }) =>
        !ignoredRoles.includes(name) &&
        name.toLowerCase().includes(searchText.toLowerCase().trim()),
    );
  };

  return {
    orgTabs,
    organisations,
    selectedCategory,
    getAllRoles,
    onCategorySelect,
    getRolesSelectionByOrg,
    updateOrganisationForRow,
    onSearch,
    selectedRolesForSearch,
    onToggleSelectedRole,
    rolesForRoleSelection,
    addOrganisationRow,
    onDeleteOrgRowClick,
  };
};

export default useOrgRow;
